import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Play, Plus, Flame, Infinity as InfinityIcon, Menu, X, Bot, Sparkles, Target, Zap,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { getServiceIcon, getAccent } from "@/lib/serviceIcons";
import AuthModal from "@/components/AuthModal";

const PLANS = [
  { name: "Starter", price: "$0", billing: "", desc: "Basic access to tools and self-serve services.", tag: { icon: InfinityIcon, text: "Unlimited Available", cls: "bg-zinc-800/50 text-zinc-400 border-zinc-700/50" }, popular: false },
  { name: "Professional", price: "$500", billing: "/mo", desc: "Includes advanced tools and prioritized support.", tag: { icon: Flame, text: "Only 9 Spots Left", cls: "bg-orange-500/10 text-orange-400 border-orange-500/30" }, popular: true },
  { name: "Elite", price: "$3000", billing: "/mo", desc: "Includes selected managed services and AI tools.", tag: { icon: Flame, text: "Only 4 Spots Left", cls: "bg-orange-500/10 text-orange-400 border-orange-500/30" }, popular: false },
  { name: "Enterprise", price: "$7500", billing: "/mo", desc: "All-inclusive VIP access to all services and tools.", tag: { icon: Flame, text: "Only 3 Spots Left", cls: "bg-orange-500/10 text-orange-400 border-orange-500/30" }, popular: false },
];

const WORDMARKS = ["amazon", "google", "slack", "stripe", "hubspot"];

export default function Landing() {
  const { profile } = useApp();
  const navigate = useNavigate();
  const [showOnboard, setShowOnboard] = useState(false);
  const [authMode, setAuthMode] = useState("signup");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.getServices().then(setServices).catch(() => setServices([]));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const enter = (mode = "signup") => {
    if (profile) { navigate("/app"); return; }
    setAuthMode(mode);
    setShowOnboard(true);
  };

  const enterProspecting = () => {
    if (profile) { navigate("/app/prospecting"); return; }
    try { localStorage.setItem("dondrae_intent", "prospecting"); } catch { /* ignore */ }
    setAuthMode("signup");
    setShowOnboard(true);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenu(false);
  };

  return (
    <div className="min-h-screen cubes-bg text-white selection:bg-primary-500 selection:text-white">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav border-b border-white/10 py-3" : "py-5 bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="text-2xl font-extrabold tracking-tight font-display cursor-pointer" onClick={() => scrollTo("top")}>
            DONDR<span className="text-primary-400">Á</span>E
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-400">
            <button onClick={() => scrollTo("features")} className="hover:text-white transition-colors">Services</button>
            <button onClick={() => scrollTo("pricing")} className="hover:text-white transition-colors">Memberships</button>
            <button onClick={() => enter("signin")} className="hover:text-white transition-colors">Sign In</button>
            <button onClick={() => enter("signup")} className="px-5 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-bold transition-all glow-box border border-primary-400/50">
              Get Started
            </button>
          </div>
          <button className="md:hidden text-white" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X /> : <Menu />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden glass-nav border-t border-white/10 mt-3 px-6 py-5 flex flex-col gap-4 text-zinc-300">
            <button onClick={() => scrollTo("features")} className="text-left">Services</button>
            <button onClick={() => scrollTo("pricing")} className="text-left">Memberships</button>
            <button onClick={() => enter("signin")} className="text-left">Sign In</button>
            <button onClick={() => enter("signup")} className="px-5 py-3 bg-primary-600 rounded-xl font-bold text-white">Get Started</button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <div id="top" className="pt-40 pb-20 px-6 max-w-7xl mx-auto text-center fade-in relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-600/20 rounded-full blur-[120px] -z-10 pointer-events-none" />

        <div className="mb-12 relative mx-auto rounded-3xl overflow-hidden glass-panel border border-white/10 shadow-[0_0_60px_rgba(59,130,246,0.15)] group max-w-5xl">
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent z-10 pointer-events-none" />
          <img src="https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=1600&q=80" alt="DONDRÁE marketing and business management platform for growing businesses" className="w-full object-cover object-center max-h-[500px] group-hover:scale-105 transition-transform duration-[1500ms] ease-out opacity-80" />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 bg-black/60 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 flex items-center shadow-2xl">
            <span className="flex h-3 w-3 relative mr-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
            </span>
            <span className="text-sm font-bold text-white tracking-widest uppercase">Now Open</span>
          </div>
        </div>

        <div className="inline-flex items-center justify-center px-4 py-1.5 glass-panel rounded-full mb-8">
          <span className="text-xs font-bold text-primary-400 uppercase tracking-widest flex items-center">
            <span className="w-2 h-2 rounded-full bg-primary-500 mr-2 animate-pulse" />
            The New Standard for Business Growth
          </span>
        </div>

        <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white tracking-tight mb-8 leading-tight max-w-5xl mx-auto font-display">
          Scale Your Business with <br className="hidden md:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 via-indigo-400 to-purple-400 glow-text">DONDRÁE</span>
        </h1>

        <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
          A marketing and business management platform. Automate workflows, access premium marketing services, and get live leads from one partner.
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button onClick={() => scrollTo("pricing")} className="px-8 py-4 bg-primary-600 text-white rounded-xl text-lg font-bold hover:bg-primary-500 transition-all glow-box flex items-center justify-center border border-primary-400/50">
            View Memberships <ArrowRight size={20} className="ml-3" />
          </button>
          <button onClick={() => enter("signup")} className="px-8 py-4 glass-panel text-white rounded-xl text-lg font-bold hover:bg-white/10 transition-all flex items-center justify-center">
            <Play size={18} className="mr-3 text-primary-400" /> Watch Demo
          </button>
        </div>
      </div>

      {/* Social proof */}
      <div className="border-y border-white/5 bg-black/20 py-10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-zinc-500 uppercase tracking-[0.2em] mb-6">Trusted by Forward-Thinking Businesses</p>
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-24 opacity-40 hover:opacity-70 transition-all duration-500">
            {WORDMARKS.map((w) => (
              <span key={w} className="text-2xl md:text-3xl font-bold lowercase tracking-tight text-zinc-300 font-display">{w}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div id="features" className="py-32 px-6 max-w-7xl mx-auto relative">
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight font-display">Services built with you, for you.</h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto font-light">Every DONDRÁE service is fully managed by our team — you share your vision, build your package, and we bring it to life.</p>
        </div>

        {/* Featured: Prospecting Agent (self-serve) */}
        <div className="mb-12 rounded-3xl overflow-hidden border border-primary-500/30 bg-gradient-to-br from-primary-900/40 via-indigo-900/30 to-black/40 relative" data-testid="prospecting-promo">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary-600/20 blur-3xl pointer-events-none" />
          <div className="relative p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold uppercase tracking-wider text-primary-200 mb-5">
                <Sparkles size={13} /> New · Self-Serve AI
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 font-display flex items-center gap-3">
                <Bot size={32} className="text-primary-300" /> Prospecting Agent
              </h3>
              <p className="text-zinc-300 leading-relaxed mb-6">
                Your always-on AI sales rep. Build your own agent in minutes — tell it who you want to reach and what you offer, and <b className="text-white">DONDRÁE-bot</b> finds prospects, writes personalized outreach, and works replies 24/7. You only pay for <b className="text-white">qualified leads</b>.
              </p>
              <div className="space-y-2.5 mb-8">
                {[[Zap, "Build your agent with a few quick questions — live in minutes"], [Target, "AI scores leads & drafts personalized emails automatically"], [Bot, "Handles replies 24/7, with human takeover whenever you want"]].map(([Icon, t]) => (
                  <div key={t} className="flex items-start gap-3 text-sm text-zinc-300"><Icon size={16} className="text-primary-300 mt-0.5 flex-shrink-0" /> {t}</div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <button onClick={enterProspecting} className="px-6 py-3.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-bold transition-colors flex items-center shadow-lg shadow-primary-900/40" data-testid="start-prospecting-btn">
                  Launch Your Agent <ArrowRight size={16} className="ml-2" />
                </button>
                <div className="text-sm text-zinc-400">One-time activation, then <span className="text-white font-bold">a small fee per qualified lead</span></div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="rounded-2xl glass-panel border border-white/10 p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-lg bg-primary-600/20 flex items-center justify-center"><Bot size={18} className="text-primary-300" /></div><span className="text-white font-bold text-sm">Your Agent</span></div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-500/15 text-green-300">Active 24/7</span>
                </div>
                {[["Jordan — Denver Realty", "QUALIFIED", "bg-purple-500/15 text-purple-300"], ["Maya — Peak Catering Co.", "CALL REQUESTED", "bg-green-500/15 text-green-300"], ["Chris — Skyline Events", "CONTACTED", "bg-blue-500/15 text-blue-300"]].map(([n, s, cls]) => (
                  <div key={n} className="flex items-center justify-between rounded-lg bg-black/30 border border-white/5 px-3 py-2.5">
                    <span className="text-xs text-zinc-300">{n}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${cls}`}>{s}</span>
                  </div>
                ))}
                <div className="text-[11px] text-zinc-500 pt-1">3 new qualified leads this week</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = getServiceIcon(s.icon);
            const accent = getAccent(s.accent);
            const from = s.packages && s.packages.length ? Math.min(...s.packages.map((p) => p.price)) : s.price;
            return (
              <div key={s.slug} className="p-8 rounded-2xl glass-panel group flex flex-col">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 border group-hover:scale-110 transition-transform ${accent.bg} ${accent.text} ${accent.border}`}>
                  <Icon size={26} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed flex-1">{s.short_desc}</p>
                <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider block">Starting at</span>
                    <span className="text-2xl font-extrabold text-white">${from.toLocaleString()}</span>
                  </div>
                  <button onClick={() => enter("signup")} className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-bold transition-colors border border-white/5 flex items-center">Get Started <ArrowRight size={14} className="ml-1.5" /></button>
                </div>
              </div>
            );
          })}
          <div className="p-8 rounded-2xl bg-gradient-to-br from-primary-900/50 to-indigo-900/50 border border-primary-500/30 text-white shadow-lg flex flex-col justify-center items-center text-center relative overflow-hidden group">
            <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center mb-6 border border-white/20 group-hover:scale-110 transition-transform">
              <Plus size={26} />
            </div>
            <h3 className="text-xl font-bold mb-3">More Services Coming Soon</h3>
            <p className="text-primary-200 text-sm leading-relaxed">We're continuously creating new services with our clients to help your business scale.</p>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div id="pricing" className="py-32 relative border-t border-white/5 bg-black/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight font-display">Choose Your Membership</h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto font-light">Select a plan to instantly access our suite of tools and begin building your custom business profile.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PLANS.map((p) => {
              const TagIcon = p.tag.icon;
              return (
                <div key={p.name} className={`glass-panel rounded-2xl p-8 flex flex-col h-full relative overflow-hidden ${p.popular ? "border-primary-500/50 shadow-[0_0_30px_rgba(59,130,246,0.15)]" : ""}`}>
                  {p.popular && <div className="absolute top-0 right-0 bg-primary-600 text-white text-[10px] font-bold px-4 py-1.5 rounded-bl-xl uppercase tracking-widest">Popular</div>}
                  <h3 className="text-2xl font-bold text-white tracking-tight">{p.name}</h3>
                  <p className="text-zinc-400 text-sm mt-3 mb-8 flex-1 leading-relaxed">{p.desc}</p>
                  <div className="mb-8">
                    <span className="text-5xl font-extrabold text-white">{p.price}</span>
                    {p.billing && <span className="text-zinc-500 text-sm font-medium">{p.billing}</span>}
                    <div className="mt-4 flex items-center">
                      <div className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${p.tag.cls}`}>
                        <TagIcon size={11} className="mr-1.5" /> {p.tag.text}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => enter("signup")} className={`w-full py-3.5 rounded-xl font-bold transition-all ${p.popular ? "bg-primary-600 text-white hover:bg-primary-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]" : "bg-white/10 text-white hover:bg-white/20 border border-white/5"}`}>
                    Select {p.name}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6 bg-black/40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-xl font-extrabold font-display">DONDR<span className="text-primary-400">Á</span>E</div>
          <p className="text-sm text-zinc-500">© 2026 DONDRÁE. Marketing & Business Management Platform.</p>
        </div>
      </footer>

      <AuthModal open={showOnboard} mode={authMode} onClose={() => setShowOnboard(false)} />
    </div>
  );
}
